import React, { useState, useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import Modal from "./modal";
import FormTitle, { StyledFormTitle } from "components/formTitle";
import InputGroup from "components/inputGroup";
import Button, { StyledButton } from "components/button";
import logger from "utils/logger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShare } from "@fortawesome/free-solid-svg-icons";
import { hoverTips } from "utils/mixin";
import notifier from "utils/notifier";

const StyledPluginInfoModal = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 25px;
`;

const StyledMiddle = styled.div`
  align-self: center;
  justify-self: center;
  color: ${({ theme }) => theme.black};

  ${({ theme, tips }) => hoverTips(theme, tips)}
`;

const StyledButtonGroup = styled.div`
  grid-column: 1/4;
  align-self: center;
  justify-self: center;
`;

const StyledAppId = styled.div`
  grid-column: 1/4;
  align-self: center;
  justify-self: center;
  text-align: center;
  margin-top: -50px;
  width: 100%;
  word-break: break-all;
  white-space: pre-wrap;
`;

const StyledDoublePluginInfoModal = styled.div`
  display: grid;
  grid-template-rows: 50px 30px 1fr 50px;
  grid-template-columns: 1fr 60px 1fr;
  row-gap: 20px;

  ${StyledFormTitle} {
    grid-column: 1/4;
    align-self: center;
  }

  ${StyledPluginInfoModal} {
    padding: 0 30px;
  }

  /* ${StyledButton} {
    width: 70px;
  } */
`;

const StyledJsonTextareaBar = styled.div`
  display: ${({ show }) => (show ? "block" : "none")};
  z-index: 1002;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 32px 32px 16px;
  background-color: ${({ theme }) => theme.white};
  border-radius: 5px;
  box-shadow: ${({ theme }) => theme.boxShadow};

  .textarea {
    min-width: 650px;
    min-height: 500px;
    max-width: 70vw;
    max-height: 80vh;
    outline: none;
    border: 1px solid #b9b9b9;
  }

  .buton_group {
    display: flex;
    justify-content: center;
    & > :first-child {
      margin-right: 8px;
    }
  }
`;

const StyledTextButton = styled.span`
  cursor: pointer;
  position: absolute;
  top: 83px;
  right: 60px;
  color: #f8af88;
  font-size: ${({ theme }) => theme.fontSize.lsmall};
  background-color: #ffffff;
  z-index: 1;
`;

const defaultFormNoAppId = {
  module: "",
  installPath: "",
  info: "",
  aliasName: "",
};

const PluginInfoModal = ({
  errorMessage,
  visible,
  type,
  data,
  displayTargetPlugin = false,
  onClose,
  onPluginAdd,
  onPluginUpdate,
}) => {
  const [form, setForm] = useState({
    appId: "",
    module: "",
    installPath: "",
    info: "",
    aliasName: "",
  });

  const [showJsonBar, setShowJsonBar] = useState(false);
  const [json, setJson] = useState("");

  useEffect(() => {
    if (type === "add") {
      setForm({
        appId: "",
        ...defaultFormNoAppId,
      });
    } else if (data && type === "update") {
      logger.log("是修改插件信息：", data);
      const { appId, info, installPath, module, aliasName } = data;
      setForm({ appId, info, installPath, module, aliasName });
    }
  }, [data, type]);

  const handleValueChange = useCallback(
    (name, value) => {
      const copy = { ...form };
      copy[name] = value;
      setForm(copy);
    },
    [form]
  );

  const handlePluginAdd = useCallback(async () => {
    logger.log("添加插件的表单信息： ", form);
    const flag = await onPluginAdd(form);
    if (flag) {
      setForm({
        appId: "",
        ...defaultFormNoAppId,
      });
    }
  }, [form, onPluginAdd]);

  const handlePluginUpdate = useCallback(async () => {
    const targetAppId = data?.appId;
    logger.log("修改插件的表单信息： ", targetAppId, form);

    await onPluginUpdate(targetAppId, form);
  }, [data, form, onPluginUpdate]);

  // 根据type变化
  const typeVariants = {
    add: { title: "添加插件", label: "Add", submit: handlePluginAdd },
    update: { title: "修改插件", label: "Update", submit: handlePluginUpdate },
  };

  const handleJson = useCallback(() => {
    try {
      const values = JSON.parse(json);
      values &&
        setForm((f) => ({
          ...f,
          appId: type === "update" ? f.appid : values.appid,
          module: values.module || "",
          installPath: values.path || "",
          info: values.name || "",
        }));
      setJson("");
      setShowJsonBar(false);
    } catch (e) {
      logger.error(e);
      notifier.error(e.message);
    }
  }, [json, type]);

  const JsonTextAreaBar = useMemo(() => {
    return (
      <StyledJsonTextareaBar show={showJsonBar}>
        <textarea
          className="textarea"
          value={json}
          onChange={(e) => setJson(e.currentTarget.value)}
        ></textarea>
        <div className="buton_group">
          <Button onClick={() => setShowJsonBar(false)}>取消</Button>
          <Button onClick={handleJson}>确定</Button>
        </div>
      </StyledJsonTextareaBar>
    );
  }, [handleJson, json, showJsonBar]);

  // 如果是修改 并且 修改时要显示目标插件
  if (type === "update" && displayTargetPlugin) {
    return (
      <Modal visible={visible} onClose={onClose}>
        {JsonTextAreaBar}
        <StyledDoublePluginInfoModal
          onKeyUp={(e) => {
            e.preventDefault();
            //阻止事件进一步传播
            e.stopPropagation();
            switch (e.keyCode) {
              //回车 提交
              case 13:
                typeVariants.update.submit();
                break;
              //Esc 取消
              case 27:
                console.log("取消");
                onClose && onClose();
                break;
              default:
                return;
            }
          }}
        >
          <FormTitle>{typeVariants.update.title}</FormTitle>
          <StyledAppId>{data?.appId}</StyledAppId>
          <StyledPluginInfoModal>
            {/* <InputGroup
              width="100%"
              label="id"
              type="text"
              name="appId"
              value={data?.appId}
              placeholder="appId"
              disabled
            /> */}
            <InputGroup
              width="100%"
              label="插件名称"
              type="text"
              name="module"
              value={data?.module}
              placeholder="module"
              disabled
            />
            <InputGroup
              width="100%"
              label="安装路径"
              type="text"
              name="installPath"
              value={data?.installPath}
              disabled
              placeholder="installPath"
            />
            <InputGroup
              width="100%"
              label="简介"
              type="text"
              name="info"
              value={data?.info}
              disabled
              onChange={handleValueChange}
              placeholder="info"
            />
            <InputGroup
              width="100%"
              label="别名"
              type="text"
              name="aliasName"
              value={data?.aliasName}
              disabled
              onChange={handleValueChange}
              placeholder="aliasName"
            />
          </StyledPluginInfoModal>
          <StyledMiddle tips="修改为">
            <FontAwesomeIcon icon={faShare} size="lg" />
          </StyledMiddle>
          <StyledPluginInfoModal>
            <StyledTextButton onClick={() => setShowJsonBar(true)}>
              使用Json填入
            </StyledTextButton>
            {/* <InputGroup
              width="100%"
              label="id"
              type="text"
              name="appId"
              value={form.appId}
              onChange={handleValueChange}
              placeholder="appId"
            /> */}
            <InputGroup
              width="100%"
              label="新插件名称"
              type="text"
              name="module"
              value={form.module}
              onChange={handleValueChange}
              placeholder="module"
            />
            <InputGroup
              width="100%"
              label="新安装路径"
              type="text"
              name="installPath"
              value={form.installPath}
              onChange={handleValueChange}
              placeholder="installPath"
            />
            <InputGroup
              width="100%"
              label="新简介"
              type="text"
              name="info"
              value={form.info}
              onChange={handleValueChange}
              placeholder="info"
            />
            <InputGroup
              width="100%"
              label="新别名(可选)"
              type="text"
              name="aliasName"
              value={form.aliasName}
              onChange={handleValueChange}
              placeholder="aliasName"
            />
          </StyledPluginInfoModal>
          <StyledButtonGroup>
            <Button message={errorMessage} onClick={typeVariants.update.submit}>
              {typeVariants.update.label}
            </Button>
          </StyledButtonGroup>
        </StyledDoublePluginInfoModal>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} onClose={onClose}>
      {JsonTextAreaBar}
      <StyledPluginInfoModal
        onKeyUp={(e) => {
          e.preventDefault();
          //阻止事件进一步传播
          e.stopPropagation();
          switch (e.keyCode) {
            //回车 提交
            case 13:
              typeVariants[type] && typeVariants[type].submit();
              break;
            //Esc 取消
            case 27:
              console.log("取消");
              onClose && onClose();
              break;
            default:
              return;
          }
        }}
      >
        <StyledTextButton onClick={() => setShowJsonBar(true)}>
          使用Json填入
        </StyledTextButton>
        <FormTitle>{typeVariants[type]?.title}</FormTitle>
        <InputGroup
          width="100%"
          label="id"
          type="text"
          name="appId"
          value={form.appId}
          onChange={handleValueChange}
          placeholder="appId"
        />
        <InputGroup
          width="100%"
          label="插件名称"
          type="text"
          name="module"
          value={form.module}
          onChange={handleValueChange}
          placeholder="module"
        />
        <InputGroup
          width="100%"
          label="安装路径"
          type="text"
          name="installPath"
          value={form.installPath}
          onChange={handleValueChange}
          placeholder="installPath"
        />
        <InputGroup
          width="100%"
          label="简介"
          type="text"
          name="info"
          value={form.info}
          onChange={handleValueChange}
          placeholder="info"
        />
        <InputGroup
          width="100%"
          label="别名(可选)"
          type="text"
          name="aliasName"
          value={form.aliasName}
          onChange={handleValueChange}
          placeholder="aliasName"
        />
        <Button message={errorMessage} onClick={typeVariants[type]?.submit}>
          {typeVariants[type]?.label}
        </Button>
      </StyledPluginInfoModal>
    </Modal>
  );
};

export default PluginInfoModal;
